/** In-memory onboarding wizard draft — mirrors EllaDraftContext for the enable flow. */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { suggestedPromptLabels } from 'skipify-types';
import type {
  FormalityLevel,
  GreetingTemplate,
  ProductDisplay,
  SentenceStyle,
  SuggestedPromptLabel,
  ToneProfile,
} from 'skipify-types';

import type {
  AgenticAgentSettingsCreate,
  AgenticEscalationSettings,
  AgenticPronunciationEntry,
  EllaRule,
} from '../../../store/modules/merchant/types';
import { DEFAULT_AGENT_AVATAR_SRC, DEFAULT_AGENT_VOICE_PROFILE_ID } from '../agentCatalog';
import { getDefaultEllaCreateSettings } from '../defaults';
import { DEFAULT_GUARDRAIL_TOGGLES } from './steps/GuardrailsStep';
import { DEFAULT_AGENT_TEMPLATE_ID, DEFAULT_INDUSTRY_VERTICAL_ID } from './steps/TemplateStep';
import { DEFAULT_ECOMMERCE_PLATFORM_ID, KNOWLEDGE_BASE_FEATURES } from './steps/YourStoreStep';

const ELLA_SDK_DEFAULT_BRANDING = {
  threadBackground: '#FEFEFE',
  brandAccent: '#01EAD3',
  textColor: '#1E1E1E',
};

export type AgentSetupBranding = {
  threadBackground: string;
  brandAccent: string;
  textColor: string;
};

export type AgentSetupConfig = {
  agentName: string;
  avatarSrc: string;
  tone: ToneProfile;
  voiceProfileId: string;
  customGreeting: GreetingTemplate;
  suggestedPrompts: SuggestedPromptLabel[];
  formality: FormalityLevel;
  sentenceStyle: SentenceStyle;
  mirrorSiteTerminology: boolean;
  productDisplay: ProductDisplay;
  branding: AgentSetupBranding;
};

export const DEFAULT_AGENT_SETUP_CONFIG: AgentSetupConfig = {
  agentName: 'Ella',
  avatarSrc: DEFAULT_AGENT_AVATAR_SRC,
  tone: 'warm-assured',
  voiceProfileId: DEFAULT_AGENT_VOICE_PROFILE_ID,
  customGreeting: 'shopping-concierge',
  suggestedPrompts: suggestedPromptLabels.slice(0, 3),
  formality: 'professional',
  sentenceStyle: 'balanced',
  mirrorSiteTerminology: true,
  productDisplay: 'In_Chat',
  branding: { ...ELLA_SDK_DEFAULT_BRANDING },
};

export type OnboardingTemplateDraft = {
  agentTemplateId: string;
  industryVerticalId: string;
};

export type OnboardingStoreDraft = {
  platformId: string;
  /** The selected platform's store address (Shopify domain or Squarespace site). */
  shopDomain: string;
  knowledgeToggles: Record<string, boolean>;
};

const defaultAgenticSettings = getDefaultEllaCreateSettings();

export const DEFAULT_ONBOARDING_CHECKOUT_MODE: AgenticAgentSettingsCreate['checkoutMode'] = {
  mode: 'handoff',
};

export type OnboardingDraft = {
  template: OnboardingTemplateDraft;
  store: OnboardingStoreDraft;
  agentSetup: AgentSetupConfig;
  engagement: AgenticAgentSettingsCreate['engagement'];
  escalation: AgenticEscalationSettings;
  checkoutMode: AgenticAgentSettingsCreate['checkoutMode'];
  voice: { pronunciationGlossary: AgenticPronunciationEntry[] };
  rules: EllaRule[];
  guardrails: Record<string, boolean>;
};

export const getDefaultOnboardingDraft = (): OnboardingDraft => ({
  template: {
    agentTemplateId: DEFAULT_AGENT_TEMPLATE_ID,
    industryVerticalId: DEFAULT_INDUSTRY_VERTICAL_ID,
  },
  store: {
    platformId: DEFAULT_ECOMMERCE_PLATFORM_ID,
    shopDomain: '',
    knowledgeToggles: Object.fromEntries(
      KNOWLEDGE_BASE_FEATURES.map((feature) => [feature.id, false]),
    ),
  },
  agentSetup: {
    ...DEFAULT_AGENT_SETUP_CONFIG,
    suggestedPrompts: [...DEFAULT_AGENT_SETUP_CONFIG.suggestedPrompts],
    branding: { ...DEFAULT_AGENT_SETUP_CONFIG.branding },
  },
  engagement: { ...defaultAgenticSettings.engagement },
  escalation: { ...defaultAgenticSettings.escalation! },
  checkoutMode: { ...DEFAULT_ONBOARDING_CHECKOUT_MODE },
  voice: {
    pronunciationGlossary: [...(defaultAgenticSettings.voice?.pronunciationGlossary ?? [])],
  },
  rules: [...(defaultAgenticSettings.rules ?? [])],
  guardrails: { ...DEFAULT_GUARDRAIL_TOGGLES },
});

type OnboardingDraftContextValue = {
  draft: OnboardingDraft;
  updateDraft: (updater: (prev: OnboardingDraft) => OnboardingDraft) => void;
  resetDraft: () => void;
};

const OnboardingDraftContext = createContext<OnboardingDraftContextValue | null>(null);

type ProviderProps = {
  children: React.ReactNode;
  initialDraft?: OnboardingDraft;
};

export const OnboardingDraftProvider = ({ children, initialDraft }: ProviderProps): JSX.Element => {
  const [draft, setDraft] = useState<OnboardingDraft>(initialDraft ?? getDefaultOnboardingDraft);

  const updateDraft = useCallback((updater: (prev: OnboardingDraft) => OnboardingDraft) => {
    setDraft((prev) => updater(prev));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(getDefaultOnboardingDraft());
  }, []);

  const value = useMemo(
    () => ({
      draft,
      updateDraft,
      resetDraft,
    }),
    [draft, updateDraft, resetDraft],
  );

  return (
    <OnboardingDraftContext.Provider value={value}>{children}</OnboardingDraftContext.Provider>
  );
};

export const useOnboardingDraft = (): OnboardingDraftContextValue => {
  const context = useContext(OnboardingDraftContext);
  if (!context) {
    throw new Error('useOnboardingDraft must be used within OnboardingDraftProvider');
  }
  return context;
};
